import { useExperiments, useHttpClient, useFedops } from '@wix/fe-essentials-standalone';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { pushUrl } from '../../components/InnerLink';
import {
  SearchResults,
} from '../../components/SearchResults';
import { NoResultsFound } from '../../components/SearchResults/NoResultsFound';
import {
  EXPERIMENTS,
  FEDOPS,
  KA_SEARCH_EXPANDED_LOCALES,
  LOCALES,
} from '../../constants';
import { Context } from '../../context';
import {
  getPageSeo,
} from '../../services/answers';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  PageType,
  GenerateAnswerResult,
} from '../../types';
import { withServerSideProps } from '../../utils/with-server-side-props';
import { getCategoriesPaths } from '../../utils/categories';
import { sanitize } from 'isomorphic-dompurify';
import { SearchQuestionBIParams, useSearchBarBI } from '../../components/SearchBar/bi';
import { isMobile, isTablet } from '../../utils/userAgent';
import { ContactSupport } from '../../components/ContactSupport';
import { getDefaultSearchResults, getKASearchResults } from '../../utils/pages/search/getSearchResults';
import { metaSiteSearchEditorTypeConverter } from '../../utils/sitePicker';
import { EditorPlatform } from '@wix/ambassador-cc-helpcenter-v1-related-document/types';
import { EditorType } from '@wix/ambassador-meta-site-search-web/types';

export const SEARCH_RESULTS_PAGE_SIZE = 12;

export type SearchPageProps = {
  searchResults: ArticleSearchResult;
  term: string;
  initCurrentPage: number;
  isContactSupport: boolean;
  biInputType?: string;
  editor?: EditorPlatform;
  previousPageType: PageType;
};

const itemIdKey = (term: string, page: number) => `${term}_${page}`;

const SearchPage: NextPage<SearchPageProps> = ({
  searchResults,
  term,
  initCurrentPage,
  isContactSupport,
  biInputType,
  editor,
  previousPageType,
}: SearchPageProps) => {
  const { categoriesTree, pageTemplateData, setPageTemplateData } = useContext(Context);
  const { experiments } = useExperiments({ readOnly: true });
  const httpClient = useHttpClient();
  const fedopsApi = useFedops();
  const [currentPage, setCurrentPage] = useState(initCurrentPage);
  const { searchQuestionBI } = useSearchBarBI();
  const { locale } = useRouter();
  const [currentSearchResults, setCurrentSearchResults] =
    useState<ArticleSearchResult>(searchResults);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedAnswer, setGeneratedAnswer] = useState<string | null>(null);
  const [relatedArticlesTitles, setRelatedArticlesTitles] = useState<string[]>([]);
  const [isProhibited, setIsProhibited] = useState<boolean | null>(null);
  const context = useContext(Context);
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);
  const isKnowledgeAgentSearchExpanded = experiments.enabled(EXPERIMENTS.SPECS.KA_SEARCH_EXPANDED_LOCALES) && KA_SEARCH_EXPANDED_LOCALES.includes(locale as string);
  const isKASearchEnabled = locale === LOCALES.EN || isKnowledgeAgentSearchExpanded;
  const isNextGenSearchEnabled = experiments.enabled(EXPERIMENTS.SPECS.NEXT_GEN_SEARCH) && locale === LOCALES.EN;
  const isWixDocsEnabled = locale === LOCALES.EN;
  const kaSearchWithRanker = experiments.enabled(EXPERIMENTS.SPECS.KA_SEARCH_WITH_RANKER) && locale === LOCALES.EN;
  const isContactClassifierEnabled = experiments.enabled(EXPERIMENTS.SPECS.IS_CONTACT_CLASSIFIER_ENABLED) && locale === LOCALES.EN;

  const categoryIds =
    currentSearchResults?.items.map((searchResult) => searchResult.categoryId || '') || [];

  const initNumberOfResults = searchResults ? searchResults.items.length : 0;
  const allPaths = getCategoriesPaths(categoriesTree, categoryIds);
  const shouldGenerateAnswer = isNextGenSearchEnabled &&
    locale === LOCALES.EN && !isMobileView && !isTabletView &&
    currentPage === 1 && searchResults?.topFiveArticles && searchResults?.topFiveArticles?.length > 0;

  useEffect(() => {
    if (searchResults) {
      setCurrentPage(initCurrentPage);
      setCurrentSearchResults(searchResults);
    }
  }, [searchResults, initCurrentPage, term]);

  useEffect(() => {
    setGeneratedAnswer(null);
    setRelatedArticlesTitles([]);
    setIsProhibited(null);
  }, [term]);

  const fetchSearchResults = useCallback(
    async (page: number) => {
      setIsLoading(true);

      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: term,
            pageSize: SEARCH_RESULTS_PAGE_SIZE,
            page,
            isWixDocsEnabled,
            isNextGenSearchEnabled,
            ...(isKASearchEnabled ? { useKA: 'true' } : {}),
            kaSearchWithRanker,
            editor,
          },
        }
      );
      setIsLoading(false);
      return data;
    },
    [httpClient, locale, term, isWixDocsEnabled, isNextGenSearchEnabled, isKASearchEnabled, kaSearchWithRanker]
  );

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    setPageTemplateData({ ...pageTemplateData, itemId: itemIdKey(term, page) });
    setCurrentSearchResults(await fetchSearchResults(page));
    await pushUrl(`${locale}/search?term=${term}&page=${page}`, true);
  };

  const sendSearchQuestionBI = async (generatedAnswerText?: string | null, usedArticles?: string[], isProhibitedTerm?: boolean) => {
    const searchQuestionBIParams: SearchQuestionBIParams = {
      results: searchResults?.items as ArticleSearchResultItem[],
      pageType: previousPageType,
      inputText: term,
      searchMethod: searchResults?.searchMethod,
      searchVersion: searchResults?.searchVersion,
      inputType: biInputType,
      isContactSupport,
      isProhibited: isProhibitedTerm,
      isGenerateAnswerFailed: generatedAnswerText?.length === 0 && shouldGenerateAnswer
    };

    const selectedSite = context.siteSelectorData?.selectedSite || undefined;
    await searchQuestionBI(searchQuestionBIParams, generatedAnswerText, usedArticles, selectedSite);
  };

  const generateAnswer = useCallback(async (): Promise<boolean> => {
    let isAnswerGenerated = false;
    if (searchResults && shouldGenerateAnswer) {
      setIsLoading(true);
      try {
        fedopsApi.interactionStarted(FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION);
        const { data } = await httpClient.post<GenerateAnswerResult>(
          '/api/generate-answer',
          {
            term,
            topFiveArticles: searchResults?.topFiveArticles
          });
        if (data) {
          setGeneratedAnswer(data.answer);
          setIsProhibited(data.isProhibited);
          setRelatedArticlesTitles(data.usedArticles);
          isAnswerGenerated = true;
          await sendSearchQuestionBI(data.answer, data.usedArticles, data.isProhibited);
        }
      } catch {
        setGeneratedAnswer('');
        setRelatedArticlesTitles([]);
        await sendSearchQuestionBI('');
        return false;
      } finally {
        setIsLoading(false);
        fedopsApi.interactionEnded(FEDOPS.INTERACTION_NAMES.ANSWER_GENERATION);
      }
    }
    return isAnswerGenerated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fedopsApi, httpClient, isContactSupport, searchResults, sendSearchQuestionBI, shouldGenerateAnswer, term]);

  const retryGenerateAnswer = async () => {
    setGeneratedAnswer(null);
    await generateAnswer();
  };

  useEffect(() => {
    if (!shouldGenerateAnswer) {
      void sendSearchQuestionBI();
    }
    void (async () => {
      if (generatedAnswer === null) {
        await generateAnswer();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, searchResults]);

  const newSearchResults =
    (isContactClassifierEnabled && isContactSupport) ?
      <ContactSupport searchMethod={searchResults.searchMethod as string} />
      :
      term.length > 0 &&
        currentSearchResults &&
        initNumberOfResults &&
        initNumberOfResults > 0 ? (
        <>
          <SearchResults
            term={term}
            searchResults={currentSearchResults}
            categoriesPaths={allPaths}
            currentPage={currentPage}
            onPageChange={onPageChange}
            isLoading={isLoading}
            generatedAnswer={generatedAnswer}
            relatedArticlesTitles={relatedArticlesTitles}
            isProhibited={isProhibited}
            retryGenerateAnswer={retryGenerateAnswer}
          />
        </>
      ) : (
        <NoResultsFound term={term} data={searchResults} />
      );

  return newSearchResults;
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ req, res, locale, query, experiments }) => {
    const { term, page = 1, previousPageType, inputType = '', editorType } = query;
    const editor = editorType ? metaSiteSearchEditorTypeConverter(editorType as EditorType) : null;
    const isKnowledgeAgentSearchExpanded = experiments[EXPERIMENTS.SPECS.KA_SEARCH_EXPANDED_LOCALES] === 'true' && KA_SEARCH_EXPANDED_LOCALES.includes(locale as string);
    const isKASearchEnabled = locale === LOCALES.EN || isKnowledgeAgentSearchExpanded;
    const isNextGenSearchEnabled = experiments[EXPERIMENTS.SPECS.NEXT_GEN_SEARCH] === 'true' && locale === LOCALES.EN;
    const kaSearchWithRanker = experiments[EXPERIMENTS.SPECS.KA_SEARCH_WITH_RANKER] === 'true' && locale === LOCALES.EN;
    const sanitizedTerm = sanitize(decodeURIComponent(term as string), {
      ALLOWED_TAGS: [],
    });
    const pageNumber = !isNaN(Number(page)) ? Number(page) : 1;

    const { searchResults = [], isContactSupport = false } =
      isKASearchEnabled ?
        await getKASearchResults(req.aspects, res, sanitizedTerm, pageNumber, locale, isNextGenSearchEnabled, kaSearchWithRanker, editor) :
        await getDefaultSearchResults(req.aspects, res, sanitizedTerm, pageNumber, locale);

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.SearchResults,
      res.locals.redisClient
    );

    return {
      props: {
        term: sanitizedTerm,
        pageType: PageType.SearchResults,
        pageData: { searchResults, term: sanitizedTerm },
        searchResults,
        initCurrentPage: pageNumber,
        itemId: itemIdKey(sanitizedTerm, pageNumber),
        pageSeo,
        isContactSupport,
        biInputType: inputType,
        editor,
        previousPageType: !isNaN(Number(previousPageType)) ? Number(previousPageType) : null,
      },
    };
  });

export default SearchPage;
